// src/web/client/src/components/messages/TestMessageDialog.jsx
//
// The "Test" button next to a template used to walk a completely separate
// codepath from the production send: a frontend-built AI prompt, a hand-rolled
// HTML body with a different header colour (#6058f9 vs #6366f1), no viral
// footer, etc. So the merchant's "Test" email was visibly different from what
// their customer actually received.
//
// This rewrite delegates everything to POST /api/messages/preview, which uses
// the SAME generateAIMessage + sendEmail (dryRun) used by the real webhook
// path. The dialog now renders the returned HTML in an iframe so the merchant
// sees byte-identical markup to what a real customer would receive — no send,
// no inbox round-trip required.

import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { API_BASE_URL } from "../../constants";
import { authenticatedFetch } from "@/utils/authenticatedFetch";

function getShopDomain() {
  return (
    localStorage.getItem("shop_domain") ||
    localStorage.getItem("shopDomain") ||
    "demo-shop.myshopify.com"
  );
}

export default function TestMessageDialog({ open, onClose, template }) {
  const { toast } = useToast();
  const [testData, setTestData] = React.useState({
    customer_name: "Sample Customer",
    product_name: "Awesome Product",
    order_amount: 99.99,
    order_number: `TEST-${Math.random().toString(36).substring(2, 9).toUpperCase()}`,
  });
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [preview, setPreview] = React.useState(null); // { subject, html }
  // Real-send state. Kept separate from `isProcessing` (which is preview-only)
  // so the merchant can see exactly which action is in flight and the
  // preview button doesn't grey itself out while a real send is pending.
  const [recipient, setRecipient] = React.useState("");
  const [isSending, setIsSending] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  // Reset state every time the dialog opens for a different template.
  // Preview clears (different template = different preview). Recipient is
  // re-prefilled from the shop record so the merchant doesn't have to
  // re-type their address on every open.
  React.useEffect(() => {
    if (!open) return;
    setPreview(null);
    let cancelled = false;
    (async () => {
      try {
        const shopDomain = getShopDomain();
        const res = await authenticatedFetch(
          `${API_BASE_URL}/shops?shop_domain=${encodeURIComponent(shopDomain)}`,
          { method: "GET" }
        );
        if (!res.ok || cancelled) return;
        const data = await res.json();
        // Backend exposes shop_email (current schema field). `email` is the
        // legacy field still populated on older shops; fall back to it so
        // pre-launch test stores aren't left with an empty box.
        const prefill = data?.shop_email || data?.email || "";
        if (prefill && !cancelled) setRecipient(prefill);
      } catch {
        // Silent — not fatal. The merchant can just type their email.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, template?._id]);

  const handlePreview = async () => {
    if (!template?._id) {
      toast({
        title: "Error",
        description: "Missing template",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    try {
      const shopDomain = getShopDomain();
      const res = await authenticatedFetch(
        `${API_BASE_URL}/messages/preview?shop_domain=${encodeURIComponent(shopDomain)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            template_id: template._id,
            customer_name: testData.customer_name || "Sample Customer",
            product_name: testData.product_name || "Sample Product",
            order_amount: Number(testData.order_amount) || 99.99,
            order_number: testData.order_number,
          }),
        }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast({
          title: "Preview failed",
          description: body.error || `HTTP ${res.status}`,
          variant: "destructive",
        });
        return;
      }

      const data = await res.json();
      setPreview({ subject: data.subject, html: data.html });
    } catch (err) {
      console.error("Preview error:", err);
      toast({
        title: "Preview failed",
        description: err?.message || "Could not render preview",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Lightweight email validation. Backend will re-validate; this just stops
  // the confirm dialog from opening with an obviously-broken address so the
  // merchant doesn't burn a confirmation step on a typo.
  const isValidEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || "").trim());

  // Opens the "are you sure" confirm — does NOT send yet. Sending only
  // happens after the merchant clicks the confirm button in AlertDialog,
  // because this action burns 1 message from their monthly quota.
  const handleSendClick = () => {
    if (!template?._id) {
      toast({ title: "Error", description: "Missing template", variant: "destructive" });
      return;
    }
    if (!isValidEmail(recipient)) {
      toast({
        title: "Enter a valid email",
        description: "We'll send the test email to this address.",
        variant: "destructive",
      });
      return;
    }
    setConfirmOpen(true);
  };

  const handleSendConfirmed = async () => {
    setConfirmOpen(false);
    setIsSending(true);
    try {
      const shopDomain = getShopDomain();
      const res = await authenticatedFetch(`${API_BASE_URL}/send/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          shop_domain: shopDomain,
          template_id: template._id,
          customer_email: recipient.trim(),
          customer_name: testData.customer_name || "Sample Customer",
          // order_id is optional on the backend (it falls back to
          // `manual-<timestamp>`); we pass the sample one for traceability
          // in the merchant's Messages table.
          order_id: testData.order_number,
          variables: {
            product_name: testData.product_name || "Sample Product",
            order_amount: Number(testData.order_amount) || 99.99,
            order_number: testData.order_number,
          },
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        toast({
          title: "Send failed",
          description: data?.error || data?.details || `HTTP ${res.status}`,
          variant: "destructive",
        });
        return;
      }
      // Backend returns email: { success, error? }. A 200 with email.success
      // false means we created the Message row but Resend rejected — surface
      // that as an error toast even though the HTTP status is OK.
      if (data.email && data.email.success === false) {
        toast({
          title: "Send failed",
          description: data.email.error || "Email provider rejected the send",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Test email sent",
        description: `Check ${recipient.trim()} — it may take a minute to arrive.`,
      });
    } catch (err) {
      console.error("Send-to-self error:", err);
      toast({
        title: "Send failed",
        description: err?.message || "Could not send test email",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  if (!template) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen && typeof onClose === "function") onClose();
      }}
    >
      {/*
        Layout: the dialog is a flex column capped at 90vh so it never
        outgrows the viewport. Header + footer are fixed; the middle section
        (form + iframe) is the only thing that scrolls. Before this, the
        dialog had no max-height and no overflow rules — long previews
        pushed the X button off-screen and the page scroll was locked by
        Radix, so the merchant felt the modal was "stuck". `p-0` on
        DialogContent lets us own padding per-section so the sticky header
        and footer don't double-pad.
      */}
      <DialogContent className="w-[95vw] sm:max-w-[720px] max-h-[90vh] p-0 flex flex-col gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle>Preview Email</DialogTitle>
          <DialogDescription>
            See exactly what your customer will receive. The preview uses the
            same AI generation and HTML template as a real send.
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable body — everything that can grow lives here */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Sample-data form */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="customer_name">Customer Name</Label>
              <Input
                id="customer_name"
                value={testData.customer_name}
                onChange={(e) =>
                  setTestData({ ...testData, customer_name: e.target.value })
                }
                placeholder="Sample Customer"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="product_name">Product Name</Label>
              <Input
                id="product_name"
                value={testData.product_name}
                onChange={(e) =>
                  setTestData({ ...testData, product_name: e.target.value })
                }
                placeholder="Awesome Product"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="order_amount">Order Amount (USD)</Label>
              <Input
                id="order_amount"
                type="number"
                step="0.01"
                value={testData.order_amount}
                onChange={(e) =>
                  setTestData({ ...testData, order_amount: e.target.value })
                }
                placeholder="99.99"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="order_number">Order Number</Label>
              <Input
                id="order_number"
                value={testData.order_number}
                onChange={(e) =>
                  setTestData({ ...testData, order_number: e.target.value })
                }
                placeholder="TEST-1234"
              />
            </div>
          </div>

          {/* Preview frame */}
          {preview && (
            <div className="border rounded-lg overflow-hidden">
              <div className="px-4 py-2 bg-gray-50 border-b text-sm text-gray-700 break-words">
                <strong>Subject:</strong> {preview.subject}
              </div>
              {/*
                Iframe is responsive: 55vh on average screens, capped to 480px
                so a tall monitor doesn't show a huge empty frame, and never
                shorter than 280px so the merchant always sees something
                meaningful. The iframe has its own native scrollbar for
                emails longer than its viewport.
              */}
              <iframe
                title="Email preview"
                srcDoc={preview.html}
                sandbox=""
                className="w-full block"
                style={{
                  height: "min(55vh, 480px)",
                  minHeight: 280,
                  border: "none",
                  background: "#fff",
                }}
              />
            </div>
          )}

          {/*
            Send-to-inbox section. Visually separated from the preview by a
            divider + tinted background so the merchant reads it as a
            distinct, paid action (it burns 1 message from their monthly
            quota) rather than another preview control. Helper text spells
            out the cost so they know what they're confirming.
          */}
          <div className="border-t pt-4">
            <div className="rounded-lg border bg-amber-50 border-amber-200 p-4 space-y-3">
              <div>
                <div className="text-sm font-semibold text-amber-900">
                  Send a real test to your inbox
                </div>
                <p className="text-xs text-amber-800 mt-1">
                  Sends through the real email provider so you see exactly how
                  Gmail / Outlook will render it. Uses 1 message from your
                  monthly plan.
                </p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="recipient_email" className="text-amber-900">
                  Send to
                </Label>
                <Input
                  id="recipient_email"
                  type="email"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder="you@yourstore.com"
                  className="bg-white"
                />
              </div>
            </div>
          </div>
        </div>

        {/*
          Sticky footer — always visible, always reachable.
          Order, left → right: Close (escape hatch), Send (paid action),
          Preview (primary free action). The primary indigo button sits
          rightmost so it reads as the default; Send uses a quieter outline
          treatment so it doesn't compete with Preview for the click.
        */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 px-6 py-4 border-t bg-background shrink-0">
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleSendClick}
            disabled={isSending || !recipient}
            className="border-amber-300 text-amber-900 hover:bg-amber-50"
          >
            {isSending ? "Sending..." : "Send to my email"}
          </Button>
          <Button
            onClick={handlePreview}
            disabled={isProcessing}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {isProcessing
              ? "Rendering..."
              : preview
              ? "Refresh preview"
              : "Generate preview"}
          </Button>
        </div>
      </DialogContent>

      {/*
        Confirmation. Real send → 1 message burned → no undo. AlertDialog
        is the right Radix primitive here because it forces a deliberate
        click rather than auto-dismissing on overlay click the way the
        outer Dialog does.
      */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send test email?</AlertDialogTitle>
            <AlertDialogDescription>
              We'll send a real email to{" "}
              <span className="font-medium">{recipient}</span> using your
              configured email provider. This uses{" "}
              <span className="font-medium">1 message</span> from your monthly
              plan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSendConfirmed}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Send
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
